import type { PostgrestError } from '@supabase/supabase-js'
import { supabase, viewAs } from '@/lib/supabase'

/** An error from the database, with a message that can be shown to the user as-is. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly code: string | undefined,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** Validation, permission and constraint errors will never succeed on retry. */
  get permanent() {
    return /^(42|22|23|P0)/.test(this.code ?? '')
  }
}

export function toApiError(error: PostgrestError | { message: string; code?: string }) {
  const code = error.code
  let message = error.message
  if (code === '42501' && /permission denied|not allowed|row-level/i.test(message)) {
    message = viewAs.get()
      ? 'View as is read-only. Stop viewing as someone else to do this as yourself.'
      : 'You do not have access to do that.'
  } else if (code === '23505') {
    message = 'That already exists.'
  } else if (/Failed to fetch|NetworkError|Load failed/i.test(message)) {
    message = 'No connection. Try again when you have signal.'
  }
  return new ApiError(message, code)
}

/** Call a database function. Every read and write in the app goes through these RPCs. */
export async function rpc<T>(fn: string, args?: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.rpc(fn, args)
  if (error) throw toApiError(error)
  return data as T
}
